import { AddOutlined } from '@mui/icons-material';
import { Box, Divider, Stack, Typography } from '@mui/material';

import { Sector } from '@/domain/impact/impact.entity.ts';
import { language } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { Fonts } from '@/ui/theme/fonts';

import { TopicScoreDetailsData } from '../type';

export function ItemsList({
  data,
  onClick,
}: {
  data: TopicScoreDetailsData;
  onClick?: (id: string, sector?: Sector) => void;
}) {
  return (
    <Stack spacing={6}>
      {data.sections.map((section) => (
        <Section key={section.name} section={section} onClick={onClick} />
      ))}
    </Stack>
  );
}

function Section({
  section,
  onClick,
}: {
  section: {
    name: string;
    sector?: Sector;
    score?: number;
    maxScore?: number;
    items: { id: string; name: string; value: number; unit: string }[];
  };
  onClick?: (id: string, sector?: Sector) => void;
}) {
  return (
    <Box key={section.name}>
      {section.name && (
        <SectionTitle
          title={section.name}
          score={section.score}
          maxScore={section.maxScore}
        />
      )}
      <Stack direction={'column'} divider={<Divider />} spacing={2}>
        {section.items.map((sectionItem) => (
          <SectionItem
            key={sectionItem.name}
            item={sectionItem}
            sector={section.sector}
            onClick={onClick}
          />
        ))}
      </Stack>
    </Box>
  );
}

function SectionTitle({
  title,
  score,
  maxScore,
}: {
  title: string;
  score?: number;
  maxScore?: number;
}) {
  return (
    <>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        gap={1}
      >
        <Typography sx={{ fontSize: '1.25rem', fontWeight: '900' }}>
          {title}
        </Typography>
        {score !== undefined && maxScore !== undefined && (
          <Typography sx={{ fontSize: '1.25rem', fontWeight: '900' }}>
            {`${score}/${maxScore}`}
          </Typography>
        )}
      </Stack>
      <Divider sx={{ my: 2 }} />
    </>
  );
}

function SectionItem({
  item,
  sector,
  onClick,
}: {
  item: { id: string; name: string; value: number; unit: string };
  sector?: Sector;
  onClick?: (id: string, sector?: Sector) => void;
}) {
  const { id, name, value, unit } = item;

  const handleOnClick = () => {
    if (onClick) {
      onClick(id, sector);
    }
  };

  return (
    <Stack
      direction={'row'}
      justifyContent={'space-between'}
      alignItems={'center'}
    >
      <Typography
        sx={{
          fontSize: '1rem',
          // fontWeight: '700',
        }}
        onClick={handleOnClick}
      >
        {name}
      </Typography>
      <Box
        sx={{
          backgroundColor: ColorsRaw.scores.Anthracite_4,
          px: 1,
          py: 0.5,
          borderRadius: 2,
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
          cursor: onClick && 'pointer',
          '&:hover': {
            backgroundColor: onClick && ColorsRaw.scores.Antracite_4012,
          },
          // '&:hover svg': {
          //   fill: onClick && ColorsRaw.main.Navy_Secondary,
          // },
          '&:hover p': {
            color: onClick && ColorsRaw.main.Navy_Dark,
          },
        }}
        onClick={handleOnClick}
      >
        {!!onClick && !value ? (
          <>
            <AddOutlined sx={{ color: ColorsRaw.main.Navy_Dark }} />
          </>
        ) : (
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: '900',
              fontFamily: Fonts.DarkerGrotesque,
            }}
          >
            {value ? value.toLocaleString(language) : '-'} {unit}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
